import React from 'react';
import { AlertType, Destination, DetectionType, Policy, Rule } from 'Generated/schema';
import { useListDestinations } from 'Source/graphql/queries';
import intersection from 'lodash/intersection';

interface UseDetectionDestinationsProps {
  detection: Pick<Rule | Policy, 'outputIds' | 'severity' | 'analysisType'>;
}

interface UseDetectionDestinationsResponse {
  loading: boolean;
  detectionDestinations: Pick<
    Destination,
    'outputType' | 'outputId' | 'displayName' | 'defaultForSeverity'
  >[];
}

const detectionTypeDestinationAlertTypeMapping = {
  [DetectionType.Rule]: [AlertType.Rule, AlertType.RuleError],
  [DetectionType.CorrelationRule]: [AlertType.CorrelationRule, AlertType.CorrelationRuleError],
  [DetectionType.ScheduledRule]: [AlertType.ScheduledRule, AlertType.ScheduledRuleError],
  [DetectionType.Policy]: [AlertType.Policy],
};

const useDetectionDestinations = ({
  detection,
}: UseDetectionDestinationsProps): UseDetectionDestinationsResponse => {
  const { data: destinations, loading } = useListDestinations();

  const detectionDestinations = React.useMemo(() => {
    if (!detection || !destinations?.destinations) {
      return [];
    }

    if (detection.outputIds?.length) {
      return detection.outputIds
        .map(outputId => {
          return destinations.destinations.find(dest => dest.outputId === outputId);
        })
        .filter(Boolean);
    }
    return destinations.destinations.filter(dest => {
      // if destinations alert type doesn't match the detection type
      if (
        intersection(
          detectionTypeDestinationAlertTypeMapping[detection.analysisType],
          dest.alertTypes,
        ).length === 0
      ) {
        return false;
      }

      // if destinations severity doesn't match the detection severity (applies to RULE and POLICY and CORRELATION_RULE)
      if (!dest.defaultForSeverity.some(sev => sev === detection.severity)) {
        return false;
      }
      const detectionType = detection.analysisType as DetectionType;
      const isRuleOrCorrelationRule =
        detectionType === DetectionType.CorrelationRule || detectionType === DetectionType.Rule;

      if (isRuleOrCorrelationRule) {
        // if the destination has log types, but there is no match (applies to just RULE and CORRELATION_RULE)
        const matchingLogTypes = intersection((detection as Rule).logTypes, dest.logTypes);
        if (dest.logTypes.length > 0 && matchingLogTypes.length === 0) {
          return false;
        }
      }
      if (!isRuleOrCorrelationRule) {
        if (dest.logTypes.length > 0) {
          return false;
        }
      }

      return true;
    });
  }, [detection, destinations]);

  return React.useMemo(
    () => ({
      detectionDestinations,
      loading,
    }),
    [detectionDestinations, loading],
  );
};

export default useDetectionDestinations;
